'use client'

import { useEffect, useState } from 'react'

import type { FlowFieldOption, FlowRemoteOptions } from './types'
import { resolveLookupOptions } from '@/src/services/lookups-resolver.api'

interface UseRemoteOptionsReturn {
  options: FlowFieldOption[]
  loading: boolean
  error: string | null
}

const optionsCache = new Map<string, FlowFieldOption[]>()
const inFlightCache = new Map<string, Promise<FlowFieldOption[]>>()

/**
 * Fetches options for a select/radio from a remote endpoint.
 *
 * @param config  Remote options config from the flow field
 * @param parentValue  Current value of the parent field (if dependsOn is set)
 */
export function useRemoteOptions(
  config: FlowRemoteOptions | undefined,
  parentValue?: string
): UseRemoteOptionsReturn {
  const [options, setOptions] = useState<FlowFieldOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const endpoint = config?.endpoint
  const dependsOn = config?.dependsOn
  const valueKey = config?.valueKey
  const labelKey = config?.labelKey
  const extraFieldsKey = JSON.stringify(config?.extraFields ?? {})

  useEffect(() => {
    if (!endpoint || !valueKey || !labelKey) return

    // If this field depends on a parent, wait until parent has a value
    if (dependsOn && (parentValue === undefined || parentValue === null || parentValue === '')) {
      setOptions([])
      setLoading(false)
      return
    }

    let cancelled = false
    setError(null)

    const lang = window.location.pathname.split('/')[1] || 'it'
    const dependencyValue = dependsOn ? parentValue : undefined
    const parsedExtraFields = JSON.parse(extraFieldsKey) as Record<string, string>
    const cacheKey = [
      endpoint,
      lang,
      dependencyValue ?? '',
      valueKey,
      labelKey,
      extraFieldsKey
    ].join('|')

    const cachedOptions = optionsCache.get(cacheKey)
    if (cachedOptions) {
      setOptions(cachedOptions)
      setLoading(false)
      return
    }

    setLoading(true)

    const mappedPromise =
      inFlightCache.get(cacheKey) ??
      resolveLookupOptions({
        endpoint,
        lang,
        parentValue: dependencyValue
      })
        .then((data: Record<string, unknown>[]) =>
          data.map((item, index) => {
            const meta = Object.entries(parsedExtraFields).reduce<Record<string, unknown>>(
              (acc, [targetField, sourceKey]) => {
                acc[targetField] = item[sourceKey]
                return acc
              },
              {}
            )

            const rawValue = String(
              item[valueKey] ?? item.value ?? item.slug ?? item.id ?? ''
            ).trim()
            const rawLabel = String(
              item[labelKey] ?? item.label ?? item.name ?? item.nome ?? rawValue
            ).trim()
            const safeValue = rawValue || rawLabel || `option-${index}`

            return {
              value: safeValue,
              label: rawLabel || safeValue,
              slug: safeValue,
              name: rawLabel || safeValue,
              meta: Object.keys(meta).length > 0 ? meta : undefined
            }
          })
        )
        .then((mapped) => {
          optionsCache.set(cacheKey, mapped)
          return mapped
        })
        .finally(() => {
          inFlightCache.delete(cacheKey)
        })

    if (!inFlightCache.has(cacheKey)) {
      inFlightCache.set(cacheKey, mappedPromise)
    }

    mappedPromise
      .then((mapped) => {
        if (cancelled) return
        setOptions(mapped)
      })
      .catch((err: Error) => {
        if (cancelled) return
        setError(err.message)
        setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dependsOn, endpoint, labelKey, valueKey, extraFieldsKey, parentValue])

  return { options, loading, error }
}
